import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { ElationMedication } from 'store/weightManagement/weightManagement.types';

import type {
  CdsCard,
  PdmpBlockResponse,
  PrescribingPrerequisite,
  PrescribingPrerequisiteType
} from './pdmp.types';

export const PDMP_PREREQUISITE_TYPE: PrescribingPrerequisiteType = 'pdmp_attestation';

export const getPdmpPrerequisite = (
  medication: ElationMedication | null | undefined
): PrescribingPrerequisite | null => {
  if (!medication?.prescribingPrerequisites?.length) return null;
  return medication.prescribingPrerequisites.find((p) => p.type === PDMP_PREREQUISITE_TYPE) ?? null;
};

export const isControlledMedication = (medication: ElationMedication | null | undefined): boolean =>
  getPdmpPrerequisite(medication) !== null;

export const deriveStateLinkLabel = (stateCode?: string): string =>
  stateCode ? `${stateCode.toUpperCase()} PDMP` : 'PDMP portal';

export interface PdmpLinkConfig {
  label: string;
  mandatory: boolean;
  stateCode?: string;
  url?: string;
}

export const getPdmpLinkConfig = (
  medication: ElationMedication | null | undefined
): PdmpLinkConfig | null => {
  const prerequisite = getPdmpPrerequisite(medication);
  if (!prerequisite) return null;
  return {
    label: deriveStateLinkLabel(prerequisite.stateCode),
    mandatory: prerequisite.mandatory ?? false,
    stateCode: prerequisite.stateCode,
    url: prerequisite.pdmpUrl
  };
};

export const isCdsBlock = (
  err: unknown
): err is FetchBaseQueryError & { data: PdmpBlockResponse; status: 422 } => {
  if (typeof err !== 'object' || err === null) return false;
  const candidate = err as { data?: unknown; status?: unknown };
  if (candidate.status !== 422) return false;
  const data = candidate.data;
  if (typeof data !== 'object' || data === null) return false;
  const cards = (data as { data?: { cards?: unknown } }).data?.cards;
  return Array.isArray(cards);
};

export const getErrorStatus = (err: unknown): number | null => {
  if (typeof err !== 'object' || err === null) return null;
  const status = (err as { status?: unknown }).status;
  return typeof status === 'number' ? status : null;
};

export const getErrorMessage = (err: unknown): string | undefined => {
  if (typeof err !== 'object' || err === null) return undefined;
  const data = (err as { data?: { message?: string } }).data;
  return typeof data?.message === 'string' ? data.message : undefined;
};

export const getFirstControlledMedication = (
  medications: Array<ElationMedication | null | undefined>
): ElationMedication | null => {
  for (const med of medications) {
    if (isControlledMedication(med)) return med ?? null;
  }
  return null;
};

export const extractCdsCards = (response: unknown): CdsCard[] => {
  if (typeof response !== 'object' || response === null) return [];
  const cards = (response as { cdsCards?: unknown }).cdsCards;
  return Array.isArray(cards) ? (cards as CdsCard[]) : [];
};
